import { useTypedTranslation } from '@/hooks/useTranslationWrappers'
import { FileOrigin, Space } from '@flatfile/api'
import { Icon } from '@flatfile/design-system'
import { PopoverContext } from '@flatfile/shared-ui'
import { createRef, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

const UploadIcon = styled(Icon)`
  margin: 0 10px 0 20px;
`

const SpaceName = styled.span`
  display: flex;
  justify-content: center;
`

export const useFileDragAndDrop = (
  onFileUpload: (file: File, origin?: FileOrigin) => Promise<void>,
  space: Space,
  multiple?: boolean,
  onSilentFileUpload?: (file: File, origin?: FileOrigin) => Promise<void>
) => {
  const { showPopover, hidePopover } = useContext(PopoverContext)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const dropRef = createRef<HTMLDivElement>()
  const { t } = useTypedTranslation()
  useEffect(() => {
    const cancelDefaults = (e: DragEvent) => {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
    const onDrag = () => {
      setIsDragging(true)
      showPopover({
        icon: <Icon name='upload' />,
        message: (
          <SpaceName>
            {t('files.uploadFile.popovers.onDragPopoverConfirmation')}{' '}
            <UploadIcon name='grid' /> {space.name}
          </SpaceName>
        ),
        onClose: undefined,
      })
    }
    const dropZone = dropRef.current
    const dragEnterListener = (e: DragEvent) => {
      cancelDefaults(e)
      onDrag()
    }

    const dragLeaveListener = (e: DragEvent) => {
      cancelDefaults(e)
      setIsDragging(false)
      hidePopover()
    }

    const dragOverListener = (e: DragEvent) => {
      cancelDefaults(e)
      onDrag()
    }

    const dropListener = async (e: DragEvent) => {
      cancelDefaults(e)
      setIsDragging(false)
      hidePopover()
      let dt = e.dataTransfer
      let files = dt?.files
      if (multiple) {
        if (files) {
          await Promise.all(
            [...files].map((file, index) =>
              index === 0
                ? onFileUpload(file, FileOrigin.Filesystem)
                : onSilentFileUpload?.(file, FileOrigin.Filesystem)
            )
          )
        }
      } else {
        if (files && files.length > 0) {
          const file = files[0]
          await onFileUpload(file, FileOrigin.Filesystem)
        }
      }
    }

    if (dropZone) {
      dropZone.addEventListener('dragenter', dragEnterListener)
      dropZone.addEventListener('dragleave', dragLeaveListener)
      dropZone.addEventListener('dragover', dragOverListener)
      dropZone.addEventListener('drop', dropListener)
    }
    return () => {
      if (dropZone) {
        dropZone.removeEventListener('dragenter', dragEnterListener)
        dropZone.removeEventListener('dragleave', dragLeaveListener)
        dropZone.removeEventListener('dragover', dragOverListener)
        dropZone.removeEventListener('drop', dropListener)
      }
    }
  }, [dropRef, setIsDragging, onFileUpload, space.name])

  return {
    dropRef,
    isDragging,
  }
}
